import { useCallback, useState, useRef, type ReactNode } from 'react';
import { Upload, X, FileIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatBytes } from '@/lib/utils';

interface DropzoneProps {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  className?: string;
  hint?: string;
  files?: File[];
  onRemove?: (i: number) => void;
  icon?: ReactNode;
  title?: string;
}

export function Dropzone({
  onFiles,
  accept,
  multiple = false,
  className,
  hint,
  files,
  onRemove,
  icon,
  title,
}: DropzoneProps) {
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setOver(false);
      const list = e.dataTransfer?.files;
      if (!list || !list.length) return;
      const arr = Array.from(list);
      onFiles(multiple ? arr : [arr[0]]);
    },
    [multiple, onFiles],
  );

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list || !list.length) return;
    onFiles(Array.from(list));
    e.target.value = '';
  };

  return (
    <div className={cn('space-y-3', className)}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOver(false);
        }}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={cn(
          'drop-zone group cursor-pointer flex flex-col items-center justify-center text-center',
          over && 'is-over',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleSelect}
          className="hidden"
        />
        <div className="size-12 rounded-xl bg-bg-inset border border-border flex items-center justify-center mb-3 group-hover:border-accent/40 group-hover:bg-accent/5 transition-colors">
          {icon ?? <Upload size={20} className="text-fg-subtle group-hover:text-accent transition-colors" />}
        </div>
        <div className="text-sm font-medium">{title ?? 'Drop file or click to browse'}</div>
        {hint && <div className="text-xs text-fg-subtle mt-1">{hint}</div>}
      </div>

      {files && files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((f, i) => (
            <div
              key={`${f.name}-${i}`}
              className="flex items-center gap-2 px-3 py-2 bg-bg-inset border border-border rounded-lg text-sm"
            >
              <FileIcon size={14} className="text-fg-subtle shrink-0" />
              <span className="flex-1 truncate">{f.name}</span>
              <span className="text-2xs text-fg-subtle font-mono shrink-0">{formatBytes(f.size)}</span>
              {onRemove && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(i);
                  }}
                  className="p-0.5 hover:bg-surface-hover rounded text-fg-subtle hover:text-danger transition-colors"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
